import { useEffect, useState } from "react";
import {
  fetchGiorni,
  giornoLungo,
  giornoPredefinitoReso,
  oggiIso,
  type EsitoRitiroUi,
} from "@/features/ritiri/api";
import { StrisciaGiorni } from "@/features/ritiri/StrisciaGiorni";
import type { AddressForm } from "../api";

/** Lo stesso avviso nel dialog del reso e in quello della pagina Resi. */
export const AVVISO_ESITO_IGNOTO =
  "SDA non ha risposto in tempo: controlla il ritiro sul portale Bracchi prima di riprovare (pagina Ritiri, voce Resi).";

type SceltaProps = {
  cliente: AddressForm;
  giorni: string[];
  giorno: string | null;
  onGiorno: (g: string | null) => void;
  disabled: boolean;
};

/** Stesso motivo mostrato al posto della striscia (dialog del reso) e come
 *  spiegazione del blocco (SDA rifiuta un ritiro senza telefono). */
export const AVVISO_SENZA_TELEFONO =
  "Senza il telefono del cliente il ritiro non si prenota: aggiungilo al mittente.";

/** In fondo al dialog del reso: il giorno in cui il corriere passa dal cliente.
 *  Senza il telefono del cliente SDA rifiuta la prenotazione: niente striscia
 *  scegliebile, solo il motivo — la scelta resta «Nessun ritiro». */
export function SceltaRitiroReso({ cliente, giorni, giorno, onGiorno, disabled }: SceltaProps) {
  const nome =
    cliente.company || [cliente.name, cliente.surname].filter(Boolean).join(" ") || "il cliente";
  const senzaTelefono = !(cliente.phone ?? "").replace(/\D/g, "");
  if (senzaTelefono) {
    return (
      <div>
        <p className="mb-1.5 text-[11.5px] font-bold text-ink">Ritiro dal cliente</p>
        <p className="rounded-sm bg-red-soft px-2.5 py-2 text-[12px] font-semibold text-ink">
          {AVVISO_SENZA_TELEFONO}
        </p>
      </div>
    );
  }
  return (
    <div>
      <p className="mb-1.5 text-[11.5px] font-bold text-ink">Ritiro dal cliente</p>
      <StrisciaGiorni
        etichetta="Giorno del ritiro dal cliente"
        giorni={giorni.slice(0, 8)}
        oggi={oggiIso()}
        valore={giorno}
        onChange={onGiorno}
        conNessuno
        disabled={disabled}
      />
      <p className="mt-1.5 text-[12px] text-ink">
        {giorno
          ? `Il corriere passa da ${nome}, ${cliente.address}, ${cliente.city}, ${giornoLungo(giorno)}. Nel messaggio al cliente vanno data e codice.`
          : "Nessun ritiro: il cliente consegna il pacco al corriere."}
      </p>
    </div>
  );
}

type EsitoProps = {
  ritiro: EsitoRitiroUi | null;
  onRiprova?: (data: string) => void;
  /** Una prenotazione (prima o riprova) è in volo per QUESTO reso: sola
   *  visualizzazione, niente Chiudi/backdrop/Esc/retry finché non risponde —
   *  o un doppio click manda un secondo tentativo che SDA rifiuta («già un
   *  ritiro attivo») su una prenotazione che in realtà è appena riuscita. */
  inCorso?: boolean;
};

/** Nello stato «creato ✓»: com'è andato il ritiro, e se è fallito si riprova. */
export function EsitoRitiroReso({ ritiro, onRiprova, inCorso }: EsitoProps) {
  const p = ritiro?.prenotazione;
  const fallito = Boolean(ritiro?.errore) || p?.stato === "rifiutato";
  const [giorni, setGiorni] = useState<string[]>([]);
  const [giorno, setGiorno] = useState<string | null>(null);

  useEffect(() => {
    if (!fallito || inCorso) return;
    fetchGiorni()
      .then((risposta) => {
        // ``?? []``: una risposta senza giorni non deve far esplodere il dialog.
        const g = risposta.giorni ?? [];
        setGiorni(g);
        setGiorno(giornoPredefinitoReso(g, oggiIso()));
      })
      .catch(() => setGiorni([]));
  }, [fallito, inCorso]);

  if (inCorso) {
    return (
      <p className="rounded-sm bg-amber-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
        Prenotazione del ritiro in corso…
      </p>
    );
  }
  if (!ritiro) return null;
  if (p && p.stato === "prenotato") {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="rounded-sm bg-green-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
          Ritiro {giornoLungo(p.data_ritiro)} · codice {p.codice}
        </p>
        {ritiro.daRiprova && (
          <p className="rounded-sm bg-amber-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
            Aggiorna il messaggio al cliente: ritiro del {giornoLungo(p.data_ritiro)}
            {p.codice ? `, codice ${p.codice}` : ""}.
          </p>
        )}
      </div>
    );
  }
  if (p && p.stato === "esito_ignoto") {
    return (
      <p className="rounded-sm bg-amber-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
        {AVVISO_ESITO_IGNOTO}
      </p>
    );
  }
  return (
    <div className="rounded-sm bg-red-soft px-2.5 py-2 text-[12.5px] text-ink">
      <p className="mb-2 font-semibold">
        Ritiro non prenotato: {ritiro.errore ?? p?.errore ?? "motivo non indicato"}
      </p>
      {giorni.length > 0 && onRiprova && (
        <div className="flex flex-col gap-2">
          <StrisciaGiorni
            etichetta="Nuovo giorno del ritiro"
            giorni={giorni.slice(0, 8)}
            oggi={oggiIso()}
            valore={giorno}
            onChange={setGiorno}
          />
          <button
            type="button"
            disabled={!giorno}
            onClick={() => giorno && onRiprova(giorno)}
            className="self-start rounded-sm border border-ink bg-ink px-3 py-1.5 text-[12.5px] font-bold text-on-ink disabled:cursor-not-allowed disabled:border-line-2 disabled:bg-paper-2 disabled:text-ink"
          >
            Prenota il ritiro
          </button>
        </div>
      )}
    </div>
  );
}
